import { initialize } from 'launchdarkly-js-client-sdk';
import appConfig from 'appConfig';
import featureKeys from './featureKeys';
import { createHarness, defaultHarnessKeyFromLdKey } from '@pearsontechnology/bronte-lib-harness';

/**
 * Unified feature flags: one in-memory map (`featureFlags`) keyed by LaunchDarkly-style names
 * so CDP/plugins stay stable. When `SERVE_FROM_LAUNCHDARKLY` is strictly `true` in LD, values are
 * LD-only. When false, LaunchDarkly remains the baseline stream, then Harness overlays boolean
 * snapshot keys using `defaultHarnessKeyFromLdKey` (dots → hyphens; `_`/`-` unchanged).
 *
 *
 * ---------------------------------------------------------------------------
 * LaunchDarkly decommission (future cleanup)
 * ---------------------------------------------------------------------------
 * 1. Remove `launchdarkly-js-client-sdk` import and `appConfig.getLaunchDarklyClientId()` usage.
 * 2. Delete the whole `=== LaunchDarkly` section (client, listeners, routing flag, snapshots).
 * 3. In `initializeFeatureFlags`, drop LD bootstrap; set `featureFlags` to `{}` or static defaults,
 *    then always `await setupHarness(user)` (or a renamed single-provider init).
 * 4. Remove `shouldServeMappedFlagsFromLaunchDarkly`, inline routing teardown/setup, and
 *    `featureKeys.SERVE_FROM_LAUNCHDARKLY` from `featureKeys.js` if unused.
 * 5. Drop routing flag helpers if Harness becomes the only provider.
 */
export default (() => {
  const LD_ROUTING_FLAG_KEY = featureKeys.SERVE_FROM_LAUNCHDARKLY;
  let featureFlags = {};
  let _subscriptions = [];
  let isInitialized = false;
  let isInitializedPromiseResolve;

  let isInitializedPromise = new Promise((resolve) => {
    isInitializedPromiseResolve = resolve;
  });

  // -------------------------------------------------------------------------
  // Shared
  // -------------------------------------------------------------------------

  /**
   *   item.flag === changed || item.flag === defaultHarnessKeyFromLdKey(changed)
   * That fires hyphen subscriptions when the notified key is dotted (LD-shaped), but not the reverse:
   * dotted subscriptions + hyphen Split keys (after frontend flag-set hydration) need a third check:
   *   defaultHarnessKeyFromLdKey(item.flag) === defaultHarnessKeyFromLdKey(changed)
   * Payload stays legacy: flagName is the key that actually changed; value uses the same ?? fallback.
   */
  /**
   * @param {string} featureFlagThatChanged
   * @param {boolean} [effectiveValue] When set (e.g. from harness reapply), updates `featureFlags` before callbacks run.
   */
  function notifyListeners(featureFlagThatChanged, effectiveValue) {
    if (typeof effectiveValue === 'boolean') {
      featureFlags[featureFlagThatChanged] = effectiveValue;
    }
    _subscriptions.forEach((item) => {
      const hyphenSubMatchesDottedNotify =
        item.flag === defaultHarnessKeyFromLdKey(featureFlagThatChanged);
      const sameCanonicalFlag =
        defaultHarnessKeyFromLdKey(item.flag) ===
        defaultHarnessKeyFromLdKey(featureFlagThatChanged);
      if (
        item.flag === featureFlagThatChanged ||
        hyphenSubMatchesDottedNotify ||
        sameCanonicalFlag
      ) {
        try {
          const result = item.callback(
            Object.freeze({
              flagName: featureFlagThatChanged,
              value:
                featureFlags[featureFlagThatChanged] ??
                featureFlags[defaultHarnessKeyFromLdKey(featureFlagThatChanged)],
            })
          );
          Promise.resolve(result).catch(() => unsubscribe(item.flag));
        } catch (e) {
          unsubscribe(item.flag);
        }
      }
    });
  }

  function unsubscribe(flag) {
    _subscriptions = _subscriptions.filter((sub) => sub.flag !== flag);
  }

  function warnFeatureFlagResolution(reason, featureName, detail) {
    const suffix = detail ? ` ${detail}` : '';
    /*eslint-disable-next-line no-console*/
    console.warn(`[FeatureFlags] ${reason} (featureName=${featureName})${suffix}`);
    return false;
  }

  /** Lazy so importing this module does not read `appConfig` (tests often use partial appConfig mocks). */
  let harnessSingleton = null;
  function getHarness() {
    if (!harnessSingleton) {
      harnessSingleton = createHarness({
        routingLdKey: LD_ROUTING_FLAG_KEY,
        authorizationKey: appConfig.getHarnessFMESDKKey(),
        notifyListeners,
      });
    }
    return harnessSingleton;
  }

  // -------------------------------------------------------------------------
  // LaunchDarkly — delete section when LD is decommissioned
  // -------------------------------------------------------------------------

  let ldclient = null;

  /** Only strict `true` uses LD-only snapshots; falsy (+ user) keeps the map Harness-driven. */
  function shouldServeMappedFlagsFromLaunchDarkly() {
    return featureFlags[LD_ROUTING_FLAG_KEY] === true;
  }


  function buildLaunchDarklyUser(user) {
    if (!user) {
      return { anonymous: true };
    }
    return {
      key: user.accountId || '',
      firstName: user.givenName || '',
      lastName: user.familyName || '',
    };
  }

  async function createLaunchDarklyClientAndLoadFlags(user) {
    const ldOptions = {
      evaluationReasons: true,
      bootstrap: 'localstorage',
    };
    const client = initialize(
      appConfig.getLaunchDarklyClientId(),
      buildLaunchDarklyUser(user),
      ldOptions
    );
    await client.waitForInitialization();
    return { client, flags: { ...client.allFlags() } };
  }


  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  async function initializeFeatureFlags(user) {

    if (ldclient) {
      ldclient.close();
    }
    await getHarness().teardownHarness();

    const { client, flags } = await createLaunchDarklyClientAndLoadFlags(user);
    ldclient = client;
    const serveFromLd = shouldServeMappedFlagsFromLaunchDarkly();

    if (!serveFromLd && user != null) {
      featureFlags = await getHarness().setupHarness(user);
      getHarness().registerConfigurationListenerAfterMerge(featureFlags);
    } else {
      featureFlags = flags;
    }

    if (!isInitialized) {
      isInitializedPromiseResolve();
      isInitialized = true;
    }

    _subscriptions.forEach((item) => {
      notifyListeners(item.flag);
    });

    client.on("change", (updatedFlagDetails) => {
      const featureFlagKeys = Object.keys(updatedFlagDetails);
      featureFlagKeys.forEach((key) => {
        featureFlags[key] = updatedFlagDetails[key].current;
      });

      const serveKeyFlipped = featureKeys.SERVE_FROM_LAUNCHDARKLY in updatedFlagDetails;
      const useHarness = !serveFromLd && user != null;
      if (serveKeyFlipped) {
        const nextServeLdOnly = Boolean(featureFlags[featureKeys.SERVE_FROM_LAUNCHDARKLY]);
        const wantDual = !nextServeLdOnly && user != null;
        if (wantDual !== useHarness) {
          initializeFeatureFlags(user).catch(() => {
            /*eslint-disable-next-line no-console*/
            console.warn("[FeatureFlags] reinit after serve-from-LD change failed");
          });
          return;
        }
      }

      if (useHarness && getHarness()) {
        getHarness().reapplyMappedKeysFromSplit(true, featureFlags);
        featureFlagKeys.forEach((key) => {
          notifyListeners(key);
        });
      } else {
        featureFlagKeys.forEach((key) => {
          notifyListeners(key);
        });
      }
    });
    return Object.freeze({...featureFlags});
  }

  function get() {
    if (!isInitialized) {
      throw new Error(
        "You tried to get the feature flags before the feature flag service got initialized"
      );
    }
    return Object.freeze({ ...featureFlags });
  }

  async function getWhenReady() {
    await isInitializedPromise;
    return Object.freeze({ ...featureFlags });
  }

  /**
   * @deprecated for React Components.
   * Use getFeatureFlagValue via 'hocs/enhanceWithFeatureFlagSubscription'.
   */
  function is(featureName) {
    if (!isInitialized) {
      throw new Error(`You tried to check the status of ${featureName} before the service got initialized`);
    }
    if (featureFlags.hasOwnProperty(featureName)) {
      return featureFlags[featureName];
    }
    if (shouldServeMappedFlagsFromLaunchDarkly()) {
      if (ldclient) {
        const live = ldclient.allFlags();
        if (Object.prototype.hasOwnProperty.call(live, featureName)) {
          return live[featureName];
        }
      }
      return warnFeatureFlagResolution('ld_only_unknown', featureName);
    }
    if (featureName === LD_ROUTING_FLAG_KEY) {
      return warnFeatureFlagResolution('routing_key_unknown', featureName);
    }
    const harnessResult = getHarness().getHarnessBooleanForLdStyleKey(featureName);
    if (harnessResult === null) {
      return warnFeatureFlagResolution(
        'harness_not_ready',
        featureName,
        'Harness is not available to evaluate; try again after initialization completes'
      );
    }
    if (!harnessResult.inHarness) {
      return warnFeatureFlagResolution('not_in_harness', featureName, 'flag not defined in Split');
    }
    return harnessResult.value;
  }

  function set(data) {
    const { name, isSet } = data;
    featureFlags[name] = isSet;
    notifyListeners(name);
    return featureFlags[name];
  }

  function subscribe(featureFlag, callbackFn) {
    const newSubscription = {
      flag: featureFlag,
      callback: callbackFn,
    };

    _subscriptions.push(newSubscription);

    isInitializedPromise.then(() => {
      if (!shouldServeMappedFlagsFromLaunchDarkly()) {
        getHarness().reapplyMappedKeysFromSplit(true, featureFlags);
      }
      notifyListeners(featureFlag);
    });

    return ((flag) => unsubscribe(flag));
  }

  return {
    initializeFeatureFlags,
    subscribe,
    get,
    getWhenReady,
    is,
    set,
    unsubscribe,
    shouldServeMappedFlagsFromLaunchDarkly,
  };
})();

export { featureKeys };
